"use client"

import { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { 
  en, zhCn, zhTw, es, fr, de, ja, ko, pt, ru, ar, hi, it, nl, sv, pl, tr, th, vi, id 
} from "./translations"

type Language = string

interface LanguageContextType {
  currentLanguage: Language
  setLanguage: (language: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

interface LanguageProviderProps {
  children: ReactNode
}

// 基础翻译数据（从独立文件导入）
const translations: Record<string, Record<string, string>> = {
  en,
  "zh-cn": zhCn,
  "zh-tw": zhTw,
  es,
  fr,
  de,
  ja,
  ko,
  pt,
  ru,
  ar,
  hi,
  it,
  nl,
  sv,
  pl,
  tr,
  th,
  vi,
  id,
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>("en")
  const [mounted, setMounted] = useState(false)

  // 从localStorage加载保存的语言设置
  useEffect(() => {
    setMounted(true)
    const savedLanguage = localStorage.getItem("preferred-language")
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage)
    }
  }, [])

  const setLanguage = (language: Language) => {
    // 如果语言没有变化，直接返回
    if (language === currentLanguage) return
    
    setCurrentLanguage(language)
    
    // 只在客户端保存到localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem("preferred-language", language)
    }
  }

  // 简化的翻译函数 - 只使用静态翻译
  const t = (key: string): string => {
    // 1. 使用当前语言的翻译
    if (translations[currentLanguage]?.[key]) {
      return translations[currentLanguage][key]
    }
    
    // 2. 回退到英文
    if (translations["en"][key]) {
      return translations["en"][key]
    }
    
    // 3. 返回key本身
    return key
  }

  return (
    <LanguageContext.Provider value={{ 
      currentLanguage, 
      setLanguage, 
      t
    }}>
      {children}
    </LanguageContext.Provider>
  )
}
